import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ChevronLeft,
  Eye,
  Loader2,
  Pencil,
  Sparkles,
  Wand2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { designerAiMessageToSafeHtml } from '@/lib/designer-ai-message-html';
import { readApiErrorMessage } from '@/lib/api';
import {
  analyzeFormationForFunnelAi,
  createFunnelFromAiPlan,
  proposeFunnelAiPlan,
  refineFunnelAiPlan,
  type FormationAiContext,
  type FunnelAiPlan,
} from '@/lib/funnel-ai';
import { LyrraOnePlanUpgradeCard } from '@/components/lyrraone/LyrraOnePlanUpgradeCard';
import { useLyrraOnePlan } from '@/hooks/useLyrraOnePlan';

type WizardStep = 'confirm' | 'understanding' | 'plan';

interface FunnelAiWizardModalProps {
  flowId: string;
  flowTitle: string;
  onClose: () => void;
}

const FLOW_STEPS: Array<{ key: keyof FunnelAiPlan['flow']; label: string; color: string }> = [
  { key: 'capture', label: 'Capture', color: 'border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40' },
  { key: 'sales', label: 'Vente', color: 'border-violet-300 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/40' },
  { key: 'checkout', label: 'Paiement', color: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40' },
  { key: 'thank_you', label: 'Merci', color: 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40' },
];

function FunnelPlanSchema({ plan }: { plan: FunnelAiPlan }) {
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-primary-200 bg-primary-50/80 px-4 py-3 text-sm dark:border-primary-800 dark:bg-primary-950/30">
        <p className="font-semibold text-primary-900 dark:text-primary-100">{plan.funnelName}</p>
        <p className="mt-1 text-primary-800/90 dark:text-primary-200/90">
          Modèle <strong>{plan.templateName ?? plan.templateId}</strong>
          {' · '}
          {plan.marketingAngle}
        </p>
        <p className="mt-2 text-xs text-primary-700/80 dark:text-primary-300/80">{plan.rationale}</p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-stretch">
        {FLOW_STEPS.map((step, index) => {
          const data = plan.flow[step.key];
          return (
            <React.Fragment key={step.key}>
              <div className={cn('min-w-0 flex-1 rounded-xl border p-3', step.color)}>
                <p className="text-xs font-bold uppercase tracking-wide opacity-70">{step.label}</p>
                <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{data.title}</p>
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{data.goal}</p>
                <p className="mt-2 text-xs italic text-gray-500 dark:text-gray-500">« {data.keyMessage} »</p>
              </div>
              {index < FLOW_STEPS.length - 1 && (
                <div className="hidden shrink-0 items-center sm:flex" aria-hidden>
                  <ArrowRight className="h-4 w-4 text-gray-400" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Public cible : {plan.targetAudience}
      </p>
    </div>
  );
}

export function FunnelAiWizardModal({ flowId, flowTitle, onClose }: FunnelAiWizardModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasFeature, loading: planLoading } = useLyrraOnePlan();
  const [step, setStep] = useState<WizardStep>('confirm');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [context, setContext] = useState<FormationAiContext | null>(null);
  const [understanding, setUnderstanding] = useState('');
  const [plan, setPlan] = useState<FunnelAiPlan | null>(null);
  const [corrections, setCorrections] = useState('');
  const [creating, setCreating] = useState(false);
  const [understandingMode, setUnderstandingMode] = useState<'preview' | 'edit'>('preview');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const runAnalyze = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeFormationForFunnelAi(flowId);
      setContext(result.context);
      setUnderstanding(result.understanding);
      setUnderstandingMode('preview');
      setStep('understanding');
    } catch (err: unknown) {
      setError(readApiErrorMessage(err, t('lyrraone.funnelAi.error', 'Erreur lors de l\'analyse.')));
    } finally {
      setLoading(false);
    }
  }, [flowId, t]);

  const runPropose = useCallback(async () => {
    if (!context || understanding.trim().length < 20) {
      setError(t('lyrraone.funnelAi.understandingTooShort', 'La compréhension doit contenir au moins quelques lignes.'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await proposeFunnelAiPlan(context.storeFormationId, understanding.trim());
      setPlan(result.plan);
      setCorrections('');
      setStep('plan');
    } catch (err: unknown) {
      setError(readApiErrorMessage(err, t('lyrraone.funnelAi.error', 'Erreur lors de la proposition.')));
    } finally {
      setLoading(false);
    }
  }, [context, understanding, t]);

  const runRefine = useCallback(async () => {
    if (!context || !plan || !corrections.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await refineFunnelAiPlan(
        context.storeFormationId,
        understanding.trim(),
        plan,
        corrections.trim(),
      );
      setPlan(result.plan);
      setCorrections('');
    } catch (err: unknown) {
      setError(readApiErrorMessage(err, t('lyrraone.funnelAi.error', 'Erreur lors de l\'ajustement.')));
    } finally {
      setLoading(false);
    }
  }, [context, corrections, plan, understanding, t]);

  const runCreate = useCallback(async () => {
    if (!context || !plan) return;
    setCreating(true);
    setError(null);
    try {
      const funnel = await createFunnelFromAiPlan(context.storeFormationId, plan);
      onClose();
      navigate(`/funnels/${funnel.id}/edit`);
    } catch (err: unknown) {
      setError(readApiErrorMessage(err, t('lyrraone.funnelAi.createError', 'Erreur lors de la création du funnel.')));
      setCreating(false);
    }
  }, [context, navigate, onClose, plan, t]);

  const stepIndex = step === 'confirm' ? 0 : step === 'understanding' ? 1 : 2;

  if (!planLoading && !hasFeature('expertAiAssistant')) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        role="dialog"
        aria-modal="true"
      >
        <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl dark:bg-gray-900">
          <button
            type="button"
            onClick={onClose}
            className="absolute end-4 top-4 rounded-lg p-1 text-gray-400 hover:bg-gray-100"
            aria-label={t('common.close', 'Fermer')}
          >
            <X className="h-5 w-5" />
          </button>
          <LyrraOnePlanUpgradeCard feature="expertAiAssistant" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="funnel-ai-title"
    >
      <div className={cn(
        'relative flex max-h-[min(90vh,820px)] w-full flex-col rounded-2xl bg-white shadow-xl dark:border dark:border-gray-700 dark:bg-gray-900',
        step === 'understanding' ? 'max-w-4xl' : 'max-w-2xl',
      )}>
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary-600" />
            <h2 id="funnel-ai-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('lyrraone.funnelAi.title', 'Générer un funnel de vente par IA')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={creating}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            aria-label={t('common.close', 'Fermer')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="shrink-0 border-b border-gray-100 px-6 py-3 dark:border-gray-800">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400">
            <span className={cn(stepIndex >= 1 && 'text-primary-600 dark:text-primary-400')}>
              1. {t('lyrraone.funnelAi.step1', 'Compréhension')}
            </span>
            <ArrowRight className="h-3 w-3" />
            <span className={cn(stepIndex >= 2 && 'text-primary-600 dark:text-primary-400')}>
              2. {t('lyrraone.funnelAi.step2', 'Schéma du funnel')}
            </span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {t('lyrraone.funnelAi.forFormation', 'Formation : {{title}}', { title: flowTitle })}
          </p>

          {step === 'confirm' && (
            <div className="space-y-4">
              <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                {t(
                  'lyrraone.funnelAi.confirmText',
                  'L\'assistant IA va analyser le contenu de votre formation, vous laisser corriger sa compréhension, puis proposer un tunnel de vente optimisé (capture, page de vente, paiement, remerciement). Vous pourrez demander des ajustements avant la création.',
                )}
              </p>
              <ul className="list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                <li>{t('lyrraone.funnelAi.confirmLi1', 'Analyse du parcours et des blocs de contenu')}</li>
                <li>{t('lyrraone.funnelAi.confirmLi2', 'Proposition de textes et structure marketing')}</li>
                <li>{t('lyrraone.funnelAi.confirmLi3', 'Création automatique du funnel dans LyrraOne')}</li>
              </ul>
            </div>
          )}

          {step === 'understanding' && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t(
                    'lyrraone.funnelAi.understandingHint',
                    'Voici ce que l\'IA a compris de votre formation. Corrigez ou complétez avant de passer à l\'étape suivante.',
                  )}
                </p>
                {understanding && !loading && (
                  <div className="inline-flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setUnderstandingMode('preview')}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                        understandingMode === 'preview'
                          ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800',
                      )}
                    >
                      <Eye className="h-3.5 w-3.5" />
                      {t('lyrraone.funnelAi.previewMode', 'Aperçu')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setUnderstandingMode('edit')}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                        understandingMode === 'edit'
                          ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200'
                          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800',
                      )}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      {t('lyrraone.funnelAi.editMode', 'Modifier')}
                    </button>
                  </div>
                )}
              </div>
              {loading && !understanding ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                  {t('lyrraone.funnelAi.analyzing', 'Analyse en cours…')}
                </div>
              ) : understandingMode === 'preview' ? (
                <div
                  className="max-h-[min(52vh,520px)] overflow-y-auto rounded-xl border border-gray-200 bg-slate-50/80 px-4 py-4 dark:border-gray-700 dark:bg-gray-800/50"
                >
                  <div
                    className="prose prose-sm max-w-none text-slate-800 dark:prose-invert dark:text-slate-200 prose-p:my-2 prose-headings:my-3 prose-ul:my-2 prose-li:my-0.5 prose-hr:my-4 [&_a]:break-all [&_a]:text-primary-600"
                    // eslint-disable-next-line react/no-danger -- HTML assaini (DOMPurify) depuis markdown IA
                    dangerouslySetInnerHTML={{ __html: designerAiMessageToSafeHtml(understanding) }}
                  />
                </div>
              ) : (
                <textarea
                  value={understanding}
                  onChange={(e) => setUnderstanding(e.target.value)}
                  rows={18}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 font-mono text-sm leading-relaxed dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  placeholder={t('lyrraone.funnelAi.understandingPlaceholder', 'Compréhension de la formation…')}
                />
              )}
            </div>
          )}

          {step === 'plan' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t(
                  'lyrraone.funnelAi.planHint',
                  'Schéma proposé pour votre tunnel de vente. Validez ou demandez des corrections.',
                )}
              </p>
              {loading && !plan ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                  {t('lyrraone.funnelAi.proposing', 'Conception du funnel…')}
                </div>
              ) : plan ? (
                <>
                  <FunnelPlanSchema plan={plan} />
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('lyrraone.funnelAi.correctionsLabel', 'Corrections souhaitées (optionnel)')}
                    </span>
                    <textarea
                      value={corrections}
                      onChange={(e) => setCorrections(e.target.value)}
                      rows={3}
                      placeholder={t(
                        'lyrraone.funnelAi.correctionsPlaceholder',
                        'Ex. : ton plus direct, mettre l\'accent sur le télétravail, ajouter une FAQ…',
                      )}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    />
                  </label>
                </>
              ) : null}
            </div>
          )}

          {error && (
            <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
              {error}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div>
            {step === 'understanding' && (
              <button
                type="button"
                onClick={() => { setStep('confirm'); setError(null); }}
                disabled={loading || creating}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('common.back', 'Retour')}
              </button>
            )}
            {step === 'plan' && (
              <button
                type="button"
                onClick={() => { setStep('understanding'); setError(null); }}
                disabled={loading || creating}
                className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
                {t('lyrraone.funnelAi.backToUnderstanding', 'Modifier la compréhension')}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              {t('common.cancel', 'Annuler')}
            </button>

            {step === 'confirm' && (
              <button
                type="button"
                onClick={runAnalyze}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {t('lyrraone.funnelAi.start', 'Lancer l\'analyse')}
              </button>
            )}

            {step === 'understanding' && (
              <button
                type="button"
                onClick={runPropose}
                disabled={loading || understanding.trim().length < 20}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                {t('lyrraone.funnelAi.nextSchema', 'Proposer le funnel')}
              </button>
            )}

            {step === 'plan' && plan && (
              <>
                {corrections.trim() && (
                  <button
                    type="button"
                    onClick={runRefine}
                    disabled={loading || creating}
                    className="inline-flex items-center gap-2 rounded-lg border border-primary-300 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-950/40 disabled:opacity-60"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {t('lyrraone.funnelAi.refine', 'Appliquer les corrections')}
                  </button>
                )}
                <button
                  type="button"
                  onClick={runCreate}
                  disabled={loading || creating}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  {t('lyrraone.funnelAi.create', 'Créer le funnel')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
